import { memo, useEffect, useMemo, useState } from "react";

import { AssetService } from "../../service/AssetService";

/** Shape BE trả về từ `AssetService.getBannerList` */
export type DashboardBannerApiItem = {
  order: number;
  link: string;
  images: string;
  description: string;
};

export type DashboardBannerItem = {
  order: number;
  description: string;
  link: string;
  /**
   * URL, path `/images/...`, data url hoặc base64 thuần (map từ field `images` của BE).
   */
  image: string;
};

const MAX_COLUMNS_DESKTOP = 2;
const BANNER_HEIGHT_PX = 160;
const GRID_GAP = "0.75rem";
const BREAKPOINT_MOBILE_MAX_PX = 767;

const getMaxColumnsPerRow = (): number => {
  if (typeof window === "undefined") return MAX_COLUMNS_DESKTOP;
  if (window.matchMedia(`(max-width: ${BREAKPOINT_MOBILE_MAX_PX}px)`).matches) {
    return 1;
  }
  return MAX_COLUMNS_DESKTOP;
};

const useMaxColumnsPerRow = (): number => {
  const [maxColumns, setMaxColumns] = useState(getMaxColumnsPerRow);

  useEffect(() => {
    const mobileMq = window.matchMedia(
      `(max-width: ${BREAKPOINT_MOBILE_MAX_PX}px)`,
    );

    const update = () => setMaxColumns(getMaxColumnsPerRow());

    update();
    mobileMq.addEventListener("change", update);

    return () => {
      mobileMq.removeEventListener("change", update);
    };
  }, []);

  return maxColumns;
};

const chunkBannerRows = (
  items: DashboardBannerItem[],
  maxColumnsPerRow: number,
): DashboardBannerItem[][] => {
  const rows: DashboardBannerItem[][] = [];
  for (let i = 0; i < items.length; i += maxColumnsPerRow) {
    rows.push(items.slice(i, i + maxColumnsPerRow));
  }
  return rows;
};

/** Base64 JPEG thường bắt đầu bằng `/9j/` — không được nhầm với path `/images/...` */
const isBase64Image = (value: string): boolean => {
  if (value.startsWith("data:")) return false;
  if (value.startsWith("http://") || value.startsWith("https://")) return false;
  if (value.startsWith("/9j/") || value.startsWith("/9j4")) return true;
  if (value.startsWith("iVBORw0KGgo")) return true;
  return value.length > 80 && /^[A-Za-z0-9+/=\s]+$/.test(value.trim());
};

const resolveImageSrc = (image: string): string => {
  if (image.startsWith("data:")) return image;
  if (image.startsWith("http://") || image.startsWith("https://")) return image;
  if (image.startsWith("/") && !isBase64Image(image)) return image;

  if (image.startsWith("iVBORw0KGgo")) {
    return `data:image/png;base64,${image}`;
  }

  // BE trả JPEG base64 (kể cả khi bắt đầu bằng `/9j/`)
  return `data:image/jpeg;base64,${image}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const parseBannerList = (data: unknown): DashboardBannerItem[] => {
  const rawList: unknown[] | null = Array.isArray(data)
    ? data
    : isRecord(data) && Array.isArray(data.items)
      ? data.items
      : null;

  if (!rawList) return [];

  return rawList.reduce<DashboardBannerItem[]>((acc, raw) => {
    if (!isRecord(raw)) return acc;

    const order =
      typeof raw.order === "number"
        ? raw.order
        : typeof raw.order === "string" &&
            raw.order.trim() !== "" &&
            !Number.isNaN(Number(raw.order))
          ? Number(raw.order)
          : null;
    const link = typeof raw.link === "string" ? raw.link : "";
    const description =
      typeof raw.description === "string" ? raw.description : "";
    const image =
      typeof raw.images === "string"
        ? raw.images
        : typeof raw.image === "string"
          ? raw.image
          : "";

    if (order === null || !image || !link) return acc;

    acc.push({ order, link, description, image });
    return acc;
  }, []);
};

export const DashboardBanners = memo(() => {
  const [items, setItems] = useState<DashboardBannerItem[]>([]);
  const maxColumnsPerRow = useMaxColumnsPerRow();

  useEffect(() => {
    let cancelled = false;

    AssetService.getBannerList()
      .then((data: unknown) => {
        if (cancelled) return;

        const parsed = parseBannerList(data);
        setItems(parsed);
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.log("[AssetService.getBannerList] error:", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.order - b.order),
    [items],
  );

  const bannerRows = useMemo(
    () => chunkBannerRows(sortedItems, maxColumnsPerRow),
    [sortedItems, maxColumnsPerRow],
  );

  if (sortedItems.length === 0) return null;

  return (
    <div className="col-12 flex flex-column" style={{ gap: GRID_GAP }}>
      {bannerRows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))`,
            columnGap: GRID_GAP,
          }}
        >
          {row.map((item) => (
            <a
              key={item.order}
              href={item.link}
              target="_blank"
              rel="noreferrer"
              aria-label={item.description}
              className="block"
            >
              <img
                src={resolveImageSrc(item.image)}
                alt={item.description}
                className="w-full block object-cover rounded-md"
                style={{ height: BANNER_HEIGHT_PX }}
                loading="lazy"
                decoding="async"
              />
            </a>
          ))}
        </div>
      ))}
    </div>
  );
});

DashboardBanners.displayName = "DashboardBanners";
